import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useTranslation } from "react-i18next";

const ClientStep = ({ formData, setFormData, handleNext, handleBack }) => {
  const { t } = useTranslation("parcours-client/ClientStep");

  const [errors, setErrors] = useState({});
  const [peselStatus, setPeselStatus] = useState("");

  // ✅ Mobile only tweaks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const labelProps = isMobile ? { shrink: true } : undefined;

  // renvoie "", "must11" ou "invalid"
  const validatePESEL = (pesel) => {
    if (!/^\d{11}$/.test(pesel)) return "must11";
    const weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
    let sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(pesel[i], 10) * weights[i];
    const controlDigit = (10 - (sum % 10)) % 10;
    return controlDigit === parseInt(pesel[10], 10) ? "" : "invalid";
  };

  const handlePESELChange = (value) => {
    setFormData({ ...formData, pesel: value });
    if (value.length === 11) {
      const code = validatePESEL(value);
      setPeselStatus(code === "" ? "valid" : code); // "valid" | "must11" | "invalid"
    } else {
      setPeselStatus("");
    }
  };

  const validate = () => {
    const newErrors = {};

    // PESEL
    if (!formData.pesel) {
      newErrors.pesel = t("errors.required");
    } else {
      const code = validatePESEL(formData.pesel);
      if (code === "must11") newErrors.pesel = t("errors.peselMust11");
      if (code === "invalid") newErrors.pesel = t("errors.peselInvalid");
    }

    // Identité
    if (!formData.nom) newErrors.nom = t("errors.required");
    if (!formData.prenom) newErrors.prenom = t("errors.required");

    // Contact
    if (!formData.email) newErrors.email = t("errors.required");
    if (!formData.telephone) newErrors.telephone = t("errors.required");

    // Adresse
    if (!formData.adresse) newErrors.adresse = t("errors.required");
    if (!formData.nrAdresse) {
      newErrors.nrAdresse = t("errors.required");
    } else if (!/^\s*\S+\s*\/\s*\S+\s*$/.test(formData.nrAdresse)) {
      newErrors.nrAdresse = t("errors.nrAdresseFormat");
    }
    if (!formData.ville) newErrors.ville = t("errors.required");
    if (!formData.codePostal) newErrors.codePostal = t("errors.required");
    if (!formData.pays) newErrors.pays = t("errors.required");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) handleNext();
  };

  // 🔧 Hauteur standard MUI (~56px) uniformisée
  const fieldStyle = {
    "& .MuiInputBase-root": {
      minHeight: 56
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#4eb933" },
      "&:hover fieldset": { borderColor: "#3e9e2a" },
      "&.Mui-focused fieldset": { borderColor: "#4eb933", borderWidth: 2 }
    },
    "& .MuiOutlinedInput-input": {
      padding: "16.5px 14px",
      fontSize: { xs: "1rem", md: "0.95rem" }
    },
    "& .MuiInputLabel-root": {
      fontSize: { xs: "0.95rem", md: "0.9rem" },
      lineHeight: 1.2,
      whiteSpace: "normal"
    },
    "& .MuiFormHelperText-root": {
      fontSize: { xs: "0.8rem", md: "0.75rem" }
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", px: 2, mt: 6 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "800px",
          borderRadius: "20px",
          px: { xs: 3, md: 6 },
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 5 },
          backgroundColor: "#F8F8F8",
          fontFamily: "'Poppins', sans-serif"
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: "2rem", md: "2.6rem" },
            color: "#000",
            mb: 3,
            textAlign: "center"
          }}
        >
          {t("title.part1")}{" "}
          <Box component="span" sx={{ color: "#4eb933" }}>
            {t("title.highlight")}
          </Box>
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* PESEL */}
          <TextField
            fullWidth
            label={t("fields.pesel")}
            value={formData.pesel || ""}
            onChange={(e) => handlePESELChange(e.target.value)}
            error={!!errors.pesel || peselStatus === "invalid"}
            helperText={
              errors.pesel
                ? errors.pesel
                : peselStatus === "valid"
                ? t("status.peselValid")
                : peselStatus === "invalid"
                ? t("errors.peselInvalid")
                : ""
            }
            inputProps={{ maxLength: 11, inputMode: "numeric" }}
            InputLabelProps={labelProps}
            sx={fieldStyle}
          />

          {/* Nom + Prénom */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label={t("fields.lastName")}
              value={formData.nom || ""}
              onChange={(e) =>
                setFormData({ ...formData, nom: e.target.value.toUpperCase() })
              }
              error={!!errors.nom}
              helperText={errors.nom}
              InputLabelProps={labelProps}
              sx={{ ...fieldStyle, flex: { xs: "1 1 100%", md: "1 1 0" } }}
            />
            <TextField
              label={t("fields.firstName")}
              value={formData.prenom || ""}
              onChange={(e) =>
                setFormData({ ...formData, prenom: e.target.value.toUpperCase() })
              }
              error={!!errors.prenom}
              helperText={errors.prenom}
              InputLabelProps={labelProps}
              sx={{ ...fieldStyle, flex: { xs: "1 1 100%", md: "1 1 0" } }}
            />
          </Box>

          {/* Email */}
          <TextField
            fullWidth
            label={t("fields.email")}
            type="email"
            value={formData.email || ""}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={!!errors.email}
            helperText={errors.email}
            InputLabelProps={labelProps}
            sx={fieldStyle}
          />

          {/* Téléphone */}
          <TextField
            fullWidth
            label={t("fields.phone")}
            value={formData.telephone || ""}
            onChange={(e) =>
              setFormData({ ...formData, telephone: e.target.value })
            }
            error={!!errors.telephone}
            helperText={errors.telephone}
            InputLabelProps={labelProps}
            sx={fieldStyle}
          />

          {/* Adresse + Nr rue/Nr local */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: { xs: "wrap", md: "nowrap" }
            }}
          >
            <TextField
              label={t("fields.address")}
              value={formData.adresse || ""}
              onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
              error={!!errors.adresse}
              helperText={errors.adresse}
              InputLabelProps={labelProps}
              sx={{
                ...fieldStyle,
                flex: { xs: "1 1 100%", md: "0 0 70%" },
                minWidth: 0
              }}
            />
            <TextField
              label={t("fields.nrAddress")}
              placeholder={t("placeholders.nrAddress")}
              value={formData.nrAdresse || ""}
              onChange={(e) => setFormData({ ...formData, nrAdresse: e.target.value })}
              error={!!errors.nrAdresse}
              helperText={errors.nrAdresse}
              InputLabelProps={labelProps}
              sx={{
                ...fieldStyle,
                flex: { xs: "1 1 100%", md: "0 0 28%" },
                minWidth: 0
              }}
            />
          </Box>

          {/* Code postal + Ville */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: { xs: "wrap", md: "nowrap" }
            }}
          >
            <TextField
              label={t("fields.postalCode")}
              value={formData.codePostal || ""}
              onChange={(e) => setFormData({ ...formData, codePostal: e.target.value })}
              error={!!errors.codePostal}
              helperText={errors.codePostal}
              InputLabelProps={labelProps}
              sx={{
                ...fieldStyle,
                flex: { xs: "1 1 100%", md: "0 0 28%" },
                minWidth: 0
              }}
            />
            <TextField
              label={t("fields.city")}
              value={formData.ville || ""}
              onChange={(e) =>
                setFormData({ ...formData, ville: e.target.value.toUpperCase() })
              }
              error={!!errors.ville}
              helperText={errors.ville}
              InputLabelProps={labelProps}
              sx={{
                ...fieldStyle,
                flex: { xs: "1 1 100%", md: "0 0 70%" },
                minWidth: 0
              }}
            />
          </Box>

          {/* Pays */}
          <TextField
            fullWidth
            label={t("fields.country")}
            value={formData.pays || ""}
            onChange={(e) =>
              setFormData({ ...formData, pays: e.target.value.toUpperCase() })
            }
            error={!!errors.pays}
            helperText={errors.pays}
            InputLabelProps={labelProps}
            sx={fieldStyle}
          />
        </Box>

        {/* Boutons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: { xs: "center", sm: "space-between" },
            gap: 2,
            mt: 4
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            startIcon={<ArrowBackIosIcon />}
            fullWidth
            sx={{
              textTransform: "none",
              borderRadius: "12px",
              fontWeight: 600,
              fontSize: { xs: "0.95rem", md: "1rem" },
              py: 1.2
            }}
          >
            {t("buttons.prev")}
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            endIcon={<ArrowForwardIosIcon sx={{ fontSize: 16 }} />}
            fullWidth
            sx={{
              backgroundColor: "#4eb933",
              color: "#fff",
              "&:hover": { backgroundColor: "#3e9e2a" },
              fontWeight: 700,
              borderRadius: "12px",
              textTransform: "none",
              fontSize: { xs: "0.95rem", md: "1rem" },
              py: 1.2,
              px: 3,
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)"
            }}
          >
            {t("buttons.next")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ClientStep;